import classNames from 'clsx';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { DropdownList, DropdownTheme } from 'reactApp/ui/DropdownList/DropdownList';
import { FilterLabel } from 'reactApp/ui/Header/Search/FilterLabel/FilterLabel';
import { typesOptionsList } from 'reactApp/ui/Header/Search/Search.data';
import { useSearchContent } from 'reactApp/ui/Header/Search/useSearchContent';
import { SearchIcon } from 'reactApp/ui/VKUIIcons';
import { Input } from 'semantic-ui-react';

import { SearchTooltip } from '../SearchTooltip/SearchTooltip';
import { SearchTooltipTypes } from '../SearchTooltip/SearchTooltip.data';
import { getSearchTooltipType } from '../SearchTooltip/SearchTooltip.helpers';
import { useTooltipState } from '../SearchTooltip/SearchTooltip.hooks';
import styles from './Search.css';

interface IProps {
    dropdownAvailable: boolean;
    toggleSearch: (state: boolean) => void;
    isExpanded: boolean;
    onlyExpanded?: boolean;
    storage: EStorageType;
}

export const Search = memo(({ dropdownAvailable, toggleSearch, isExpanded, storage, onlyExpanded = false }: IProps) => {
    const [searchInputRef, setInputRef] = useState(null);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const tooltipType = getSearchTooltipType();
    const onRefChange = useCallback((node) => {
        if (node && node.inputRef && node.inputRef.current) {
            setInputRef(node.inputRef);
        }
    }, []);
    const { isTooltipVisible, onTooltipClose } = useTooltipState({
        visibilityValue: !!(tooltipType && !(isExpanded || onlyExpanded) && searchInputRef),
        tooltipType,
    });

    const {
        isActive,
        isFocus,
        value,
        searchOption,
        placeholder,
        onSearchClick,
        onInputClick,
        onDocumentClick,
        onInputChange,
        onKeyUp,
        setActiveInput,
        chooseSearchType,
        removeSearchType,
        removeFocus,
    } = useSearchContent({ toggleSearch, rootRef, isExpanded, onlyExpanded, storage, dropdownRef });

    useEffect(() => {
        document.addEventListener('click', onDocumentClick, { capture: true });

        return () => {
            document.removeEventListener('click', onDocumentClick, { capture: true });
        };
    }, [onDocumentClick]);

    const optionsList = useMemo(
        () => typesOptionsList.map((item) => ({ ...item, onClick: () => chooseSearchType(item.id) })),
        [chooseSearchType]
    );

    const renderDropdownItem = useCallback(
        ({ icon, text }) => (
            <div className={styles.dropdownItem}>
                <div className={styles.dropdownIcon}>{icon}</div>
                {text}
            </div>
        ),
        []
    );

    const calcDropdownPosition = useCallback(() => {
        let posX = 0;
        let posY = 0;

        if (rootRef.current) {
            const elRect = rootRef.current.getBoundingClientRect();

            posY = elRect.top + elRect.height + window.scrollY + 8;
            posX = elRect.left + window.scrollX + 40;
        }

        return { posY, posX };
    }, []);

    return (
        <CSSTransition
            in={isExpanded}
            timeout={200}
            classNames={{
                exit: styles.animation_exit,
                exitActive: styles.animation_exit_active,
                enter: styles.animation_appear,
                enterActive: styles.animation_appear_active,
            }}
            onEntered={setActiveInput}
        >
            <div
                className={classNames(styles.root, { [styles.root_expand]: isExpanded || onlyExpanded, [styles.root_active]: isActive })}
                ref={rootRef}
            >
                {isActive && <SearchIcon className={styles.searchIcon} />}
                {searchOption && isActive && (
                    <FilterLabel id={searchOption.id} text={searchOption.text} icon={searchOption.icon} removeFilter={removeSearchType} />
                )}
                <Input
                    className={styles.input}
                    placeholder={placeholder}
                    onClick={onInputClick}
                    onChange={onInputChange}
                    value={value}
                    onKeyUp={onKeyUp}
                    focus={isFocus}
                    ref={onRefChange}
                />
                <div onClick={onSearchClick} className={styles.search}>
                    {isActive ? 'Найти' : <SearchIcon />}
                </div>
                {isFocus && !searchOption && dropdownAvailable && (
                    <DropdownList
                        dataQAId="search-options"
                        list={optionsList}
                        calcPosition={calcDropdownPosition}
                        close={removeFocus}
                        renderItem={renderDropdownItem}
                        parentRef={rootRef}
                        gaId="search"
                        closeOnScroll
                        theme={DropdownTheme.medium}
                        rootRef={dropdownRef}
                    />
                )}
                {isTooltipVisible && (
                    <SearchTooltip targetRef={searchInputRef} type={tooltipType as SearchTooltipTypes} onClose={onTooltipClose} />
                )}
            </div>
        </CSSTransition>
    );
});

Search.displayName = 'Search';
